/**
 * Club page — scroll reveal.
 * Animates [data-l-club-reveal] sections into view with GSAP.
 * IntersectionObserver owns viewport entry; GSAP owns only the motion.
 *
 * - Progressive enhancement: if GSAP is unavailable or the user prefers reduced
 *   motion, nothing is hidden and content renders normally.
 * - `data-l-club-reveal-delay` (seconds) staggers an element.
 * - Re-entrant safe for the theme editor: only unrevealed elements are touched.
 */
;(function () {
	// Several club sections on one page each request this file (a-club-assets),
	// so bail if an earlier copy already initialised.
	if (window.__lClubRevealActive) return

	// Signals to the inline prep script (a-global-styles) that this file ran, so
	// its window.load fallback only strips the hide-guard when a-club.js fails to load.
	window.__lClubRevealActive = true

	var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
	var revealObserver = null

	function initClubReveal() {
		var els = document.querySelectorAll('[data-l-club-reveal]:not(.is-revealed)')

		if (!els.length) return

		if (prefersReducedMotion || !window.gsap || !window.IntersectionObserver) {
			revealClubStatic()

			return
		}

		// Disconnect any prior observer before re-binding (theme-editor section
		// reloads call this again) so stale observers don't accumulate.
		if (revealObserver) revealObserver.disconnect()

		revealObserver = new IntersectionObserver(handleRevealEntries, {
			root: null,
			rootMargin: '0px 0px -16% 0px',
			threshold: 0.01,
		})

		els.forEach(function (el) {
			el.classList.add('is-reveal-bound')
			revealObserver.observe(el)
		})
	}

	function handleRevealEntries(entries) {
		entries.forEach(function (entry) {
			if (!entry.isIntersecting) return

			revealObserver.unobserve(entry.target)
			try {
				playSectionReveal(entry.target)
			} catch (e) {
				markRevealComplete(entry.target)
			}
		})
	}

	function playSectionReveal(el) {
		if (el.classList.contains('is-revealed') || el.classList.contains('is-revealing')) return

		var delay = parseFloat(el.dataset.lClubRevealDelay) || 0
		var config = getRevealConfig(el)
		var parts = getRevealParts(el)
		var media = getRevealMedia(el)
		var animatedTargets = [el].concat(parts, media)
		var tl = window.gsap.timeline({
			delay: delay,
			defaults: {
				ease: 'power3.out',
				overwrite: 'auto',
			},
			onComplete: function () {
				markRevealComplete(el)
				window.gsap.set(animatedTargets.length ? animatedTargets : el, {
					clearProps: 'opacity,visibility,transform,willChange',
				})
			},
		})

		el.classList.add('is-revealing')

		if (animatedTargets.length) {
			window.gsap.set(animatedTargets, { willChange: 'transform,opacity' })
		} else {
			window.gsap.set(el, { willChange: 'transform,opacity' })
		}

		tl.fromTo(el, getRevealFrom(config.shell), getRevealTo(config.shell, true), 0)

		if (parts.length) {
			tl.fromTo(parts, getRevealFrom(config.parts), getRevealTo(config.parts, true), 0.08)
		}

		if (media.length) {
			tl.fromTo(media, getRevealFrom(config.media), getRevealTo(config.media, true), config.mediaAt)
		}
	}

	function markRevealComplete(el) {
		el.classList.remove('is-reveal-bound')
		el.classList.remove('is-revealing')
		el.classList.add('is-revealed')

		if (!document.querySelector('[data-l-club-reveal]:not(.is-revealed)')) {
			document.documentElement.classList.remove('l-club-reveal-prep')
		}
	}

	function revealClubStatic() {
		document.documentElement.classList.remove('l-club-reveal-prep')

		document.querySelectorAll('[data-l-club-reveal]').forEach(function (el) {
			markRevealComplete(el)
		})
	}

	function getRevealFrom(vars) {
		var from = {}

		copyRevealProp(vars, from, 'x')
		copyRevealProp(vars, from, 'y')
		copyRevealProp(vars, from, 'scale')
		copyRevealProp(vars, from, 'transformOrigin')

		if (vars.opacity !== undefined) from.autoAlpha = vars.opacity

		return from
	}

	function getRevealTo(vars, holdClearProps) {
		var to = {}

		copyRevealProp(vars, to, 'duration')
		copyRevealProp(vars, to, 'ease')
		copyRevealProp(vars, to, 'stagger')
		copyRevealProp(vars, to, 'transformOrigin')

		if (!holdClearProps && vars.clearProps !== undefined) {
			copyRevealProp(vars, to, 'clearProps')
		}

		if (vars.opacity !== undefined) to.autoAlpha = 1
		if (vars.x !== undefined) to.x = 0
		if (vars.y !== undefined) to.y = 0
		if (vars.scale !== undefined) to.scale = 1

		return to
	}

	function copyRevealProp(source, target, prop) {
		if (source[prop] !== undefined) {
			target[prop] = source[prop]
		}
	}

	function getRevealConfig(el) {
		var config = {
			shell: {
				opacity: 0,
				y: 16,
				duration: 0.65,
				ease: 'power2.out',
				clearProps: 'opacity,transform',
			},
			parts: {
				opacity: 0,
				y: 18,
				scale: 0.99,
				duration: 0.75,
				stagger: { each: 0.055, from: 'start' },
				clearProps: 'opacity,transform',
			},
			media: {
				scale: 1.025,
				duration: 1,
				ease: 'power2.out',
				transformOrigin: '50% 55%',
				clearProps: 'transform',
			},
			mediaAt: 0,
		}

		if (el.classList.contains('l-club-hero__inner')) {
			config.shell = {
				opacity: 0,
				scale: 0.985,
				duration: 0.75,
				ease: 'power2.out',
				clearProps: 'opacity,transform',
			}
			config.parts = {
				opacity: 0,
				y: -10,
				scale: 0.985,
				duration: 0.85,
				stagger: 0.06,
				transformOrigin: '50% 50%',
				clearProps: 'opacity,transform',
			}
			config.media.scale = 1.035
			config.media.duration = 1.2
		} else if (el.classList.contains('l-club-invitation__inner')) {
			config.parts = {
				opacity: 0,
				x: function (index) {
					return index === 0 ? -16 : 14
				},
				y: function (index) {
					return index === 0 ? 0 : 8
				},
				duration: 0.78,
				stagger: { each: 0.055, from: 'start' },
				clearProps: 'opacity,transform',
			}
			config.media.scale = 1.025
			config.mediaAt = 0.05
		} else if (el.classList.contains('l-club-select__intro')) {
			config.parts = {
				opacity: 0,
				scale: 0.985,
				y: 0,
				duration: 0.72,
				stagger: 0.07,
				clearProps: 'opacity,transform',
			}
		} else if (el.classList.contains('l-club-select__options')) {
			config.parts = {
				opacity: 0,
				x: function (index) {
					return index % 2 === 0 ? -12 : 12
				},
				scale: 0.99,
				duration: 0.72,
				stagger: 0.06,
				clearProps: 'opacity,transform',
			}
		} else if (el.classList.contains('l-club-feature')) {
			config.parts = {
				opacity: 0,
				x: function (index, node) {
					if (node.classList.contains('l-club-feature__text')) return -16
					if (node.classList.contains('l-club-feature__media')) return 16

					return 0
				},
				y: function (index, node) {
					return node.classList.contains('l-club-feature__tagline') ? 10 : 0
				},
				duration: 0.8,
				stagger: { each: 0.06, from: 'start' },
				clearProps: 'opacity,transform',
			}
			config.media.scale = 1.03
			config.mediaAt = 0.12
		} else if (el.classList.contains('l-club-dedicated')) {
			config.parts = {
				opacity: 0,
				y: function (index) {
					return index === 0 ? 12 : 0
				},
				scale: function (index) {
					return index === 0 ? 0.99 : 0.985
				},
				duration: 0.78,
				stagger: { each: 0.06, from: 'start' },
				transformOrigin: '50% 55%',
				clearProps: 'opacity,transform',
			}
		} else if (el.classList.contains('l-club-exp__inner')) {
			config.parts = {
				opacity: 0,
				x: function (index) {
					return index === 0 ? 0 : index % 2 === 0 ? 12 : -12
				},
				y: function (index) {
					return index === 0 ? 10 : 0
				},
				scale: 0.99,
				duration: 0.76,
				stagger: { each: 0.055, from: 'start' },
				clearProps: 'opacity,transform',
			}
			config.media.scale = 1.025
			config.mediaAt = 0.12
		} else if (el.classList.contains('l-club-faq__inner')) {
			config.parts = {
				opacity: 0,
				x: function (index, node) {
					return node.classList.contains('l-club-faq__concierge') ? -12 : 12
				},
				y: function (index, node) {
					return node.classList.contains('l-club-faq__heading') ? 10 : 0
				},
				duration: 0.72,
				stagger: { each: 0.045, from: 'start' },
				clearProps: 'opacity,transform',
			}
			config.media.scale = 0.97
			config.mediaAt = 0.16
		}

		return config
	}

	function getRevealParts(el) {
		var selector

		if (el.classList.contains('l-club-hero__inner')) {
			selector = '.l-club-hero__title'
		} else if (el.classList.contains('l-club-invitation__inner')) {
			selector = '.l-club-invitation__media, .l-club-invitation__heading, .l-club-invitation__intro, .l-club-benefit'
		} else if (el.classList.contains('l-club-select__intro')) {
			selector = '.l-club-select__heading, .l-club-select__lead'
		} else if (el.classList.contains('l-club-select__options')) {
			selector = '.l-club-select__option'
		} else if (el.classList.contains('l-club-feature')) {
			selector = '.l-club-feature__tagline, .l-club-feature__text, .l-club-feature__media'
		} else if (el.classList.contains('l-club-dedicated')) {
			selector = '.l-club-dedicated__heading, .l-club-card'
		} else if (el.classList.contains('l-club-exp__inner')) {
			selector = '.l-club-exp__heading, .l-club-exp__card'
		} else if (el.classList.contains('l-club-faq__inner')) {
			selector = '.l-club-faq__concierge, .l-club-faq__heading, .l-club-faq__item'
		}

		return selector ? getVisibleNodes(el.querySelectorAll(selector)) : []
	}

	function getRevealMedia(el) {
		return getVisibleNodes(
			el.querySelectorAll(
				'.l-club-hero__img, .l-club-invitation__img, .l-club-feature__img, .l-club-card__img, .l-club-exp__img, .l-club-exp__video video, .l-club-faq__avatar-img'
			)
		)
	}

	function getVisibleNodes(nodes) {
		var seen = []

		return Array.prototype.filter.call(nodes, function (node) {
			if (!node || seen.indexOf(node) !== -1 || !node.getClientRects().length) return false
			seen.push(node)

			return true
		})
	}

	try {
		initClubReveal()
	} catch (e) {
		revealClubStatic()
	}

	// Theme editor: re-reveal sections re-rendered by section edits.
	if (window.Shopify && window.Shopify.designMode) {
		document.addEventListener('shopify:section:load', function () {
			document.documentElement.classList.remove('l-club-reveal-prep')
			try {
				initClubReveal()
			} catch (e) {
				revealClubStatic()
			}
		})
	}
})()

/**
 * Club carousel — a lightweight scroll-snap slider for layouts Swiper can't
 * express. The Experiences section pairs a media card on top of a text card per
 * page (CSS `grid-auto-flow: column` with two natural-height rows); Swiper's
 * grid forces equal rows, so layout + snapping live in CSS and this element only
 * drives the progress-bar fill and the prev/next arrows.
 *
 * Markup contract:
 *   <l-club-carousel>
 *     <div data-club-carousel-viewport> … cards … </div>
 *     <span data-club-carousel-fill></span>          (sets --l-club-carousel-progress)
 *     <button data-club-carousel-prev> <button data-club-carousel-next>
 */
if (!customElements.get('l-club-carousel')) {
	class LTAClubCarousel extends HTMLElement {
		connectedCallback() {
			this.viewport = this.querySelector('[data-club-carousel-viewport]')
			this.fills = Array.from(this.querySelectorAll('[data-club-carousel-fill]'))
			this.prevBtns = Array.from(this.querySelectorAll('[data-club-carousel-prev]'))
			this.nextBtns = Array.from(this.querySelectorAll('[data-club-carousel-next]'))
			if (!this.viewport) return

			this._onScroll = this._update.bind(this)
			this._onPrev = this._scrollByPage.bind(this, -1)
			this._onNext = this._scrollByPage.bind(this, 1)

			this.viewport.addEventListener('scroll', this._onScroll, { passive: true })
			window.addEventListener('resize', this._onScroll)
			this.prevBtns.forEach((btn) => btn.addEventListener('click', this._onPrev))
			this.nextBtns.forEach((btn) => btn.addEventListener('click', this._onNext))
			this._update()
		}

		disconnectedCallback() {
			if (this.viewport) this.viewport.removeEventListener('scroll', this._onScroll)
			window.removeEventListener('resize', this._onScroll)
			if (this.prevBtns) this.prevBtns.forEach((btn) => btn.removeEventListener('click', this._onPrev))
			if (this.nextBtns) this.nextBtns.forEach((btn) => btn.removeEventListener('click', this._onNext))
		}

		_scrollByPage(dir) {
			if (!this.viewport) return
			const gap = parseFloat(window.getComputedStyle(this.viewport).columnGap) || 0
			const step = this.viewport.clientWidth + gap

			this.viewport.scrollBy({ left: dir * step, behavior: 'smooth' })
		}

		_setDisabled(btn, isDisabled) {
			if (!btn) return
			btn.classList.toggle('swiper-button-disabled', isDisabled)
			btn.disabled = isDisabled
			btn.setAttribute('aria-disabled', isDisabled ? 'true' : 'false')
		}

		_update() {
			const v = this.viewport
			if (!v) return
			const max = v.scrollWidth - v.clientWidth
			const progress = max > 0 ? Math.min(1, (v.scrollLeft + v.clientWidth) / v.scrollWidth) : 1
			this.fills.forEach((fill) => fill.style.setProperty('--l-club-carousel-progress', progress))
			this.prevBtns.forEach((btn) => this._setDisabled(btn, v.scrollLeft <= 1))
			this.nextBtns.forEach((btn) => this._setDisabled(btn, v.scrollLeft >= max - 1))
		}
	}

	customElements.define('l-club-carousel', LTAClubCarousel)
}
